import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useIntl } from 'react-intl'
import { getRouteApi } from '@tanstack/react-router'
import type { AxiosError } from 'axios'

import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { EnvelopeIcon } from '@gto/ui/icons/outline'
import { EnvelopeOpenIcon } from '@gto/ui/icons/outline'
import { colors } from '@gto/ui/theme/design-tokens'
import { Typography } from '@gto/ui/typography'

import { useAccountPasswordResetCreate } from '@/api/gen'
import { FormContainer } from '@/components/form-container'
import { TextInput } from '@/components/text-input'
import { useFormValidation } from '@/hooks/use-form-validation'
import type { PasswordResetModel } from '@/pages/auth/search-schema'
import { getAxiosErrorMessage } from '@/utils/helpers'
import type { PasswordResetFormData, PasswordResetFormField } from '@/utils/validation'
import { passwordResetSchema } from '@/utils/validation'

const routeApi = getRouteApi('/_auth/login/password-reset/')

type ResetStatus = {
  status?: PasswordResetModel['status']
  message?: string
}
export const RootRoute = () => {
  const navigate = routeApi.useNavigate()
  const search = routeApi.useSearch()

  const { formatMessage } = useIntl()
  const isSmAndLower = useMediaQuery('down', 'sm')

  const { validate, validateField, errors, clearFieldError, isValid } =
    useFormValidation(passwordResetSchema)

  const [formValues, setFormValues] = useState<PasswordResetFormData>({
    email: '',
  })

  const getSearchStatus = (status?: PasswordResetModel['status']) => {
    return status === 'success' || status === 'error'
      ? {
          status: status as PasswordResetModel['status'],
          message:
            status === 'success'
              ? formatMessage({ id: 'auth.passwordReset.success.description' })
              : formatMessage({ id: 'auth.passwordReset.confirm.error' }),
        }
      : null
  }

  const [resetResult, setResetResult] = useState<ResetStatus | null>(() =>
    getSearchStatus(search.status),
  )

  const { mutate: resetPassword, isPending } = useAccountPasswordResetCreate({
    mutation: {
      onSuccess: () => {
        setResetResult({
          status: 'success',
          message:
            'If there is an active account with this email, a password reset link has been emailed. Please check your email and use the link to reset your password. The link will expire in 10 minutes.',
        })
      },
      onError: (error: AxiosError) => {
        const err = getAxiosErrorMessage(error)

        setResetResult({
          status: 'error',
          message: err || formatMessage({ id: 'auth.passwordReset.error' }),
        })
      },
    },
  })

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))

    setTimeout(() => {
      const isValidField = validateField(name as PasswordResetFormField, value)
      if (isValidField) {
        clearFieldError(name as PasswordResetFormField)
      }
    }, 300)
  }

  const onSubmit = () => {
    navigate({
      to: '.',
      search: { status: undefined },
      replace: true,
    })
      .then(() => {
        setResetResult(null)
        const isValid = validate(formValues)
        if (isValid) {
          resetPassword({ data: formValues })
        }
      })
      .catch(console.error)
  }

  return (
    <FormContainer>
      <div>
        <Typography
          gutterBottom
          className="flex items-center gap-2"
          variant={isSmAndLower ? 'h5' : 'h4'}
        >
          <EnvelopeOpenIcon
            color={colors.primary.main}
            height={36}
          />{' '}
          <FormattedMessage
            id={
              resetResult?.status === 'success'
                ? 'auth.passwordReset.success.title'
                : 'auth.passwordReset.title'
            }
          />
        </Typography>
        <Typography
          className="whitespace-pre-wrap"
          color="text.secondary"
          variant="body1"
        >
          <FormattedMessage
            id={
              resetResult?.status === 'success'
                ? 'auth.passwordReset.success.description'
                : 'auth.passwordReset.description'
            }
          />
        </Typography>
      </div>

      {resetResult?.status !== 'success' && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-8 sm:gap-6">
              <TextInput
                error={errors.email}
                label="common.labels.email"
                input={{
                  id: 'email',
                  type: 'email',
                  size: 'medium',
                  onChange,
                  disabled: isPending,
                  value: formValues.email,
                  name: 'email',
                  endAddon: (
                    <EnvelopeIcon
                      className="size-6"
                      color="var(--palette-action-active)"
                    />
                  ),
                }}
              />
            </div>
          </div>
          {resetResult?.status === 'error' && (
            <Alert
              heading={resetResult.message}
              severity="warning"
            />
          )}

          <Button
            color="primary"
            disabled={isPending || !isValid}
            loading={isPending}
            size="large"
            variant="contained"
            onClick={onSubmit}
          >
            <FormattedMessage id="common.controls.continue" />
          </Button>
        </>
      )}
    </FormContainer>
  )
}
